import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Icon, Modal } from '@evogard/ui';
import { format, fromColumn, sum } from '@evogard/domain';
import type { MyPayment, MyPayments } from '@evogard/contract';
import { InvoiceForm, PaymentStatusBadge } from '../../components/PaymentParts.js';
import { MyPaymentModal } from '../financeiro/PaymentsScreen.js';

/*
 * O PAGAMENTO de um lote no cartão "Recorrência 1x/2x" do Início (28/10/2026,
 * pedido do dono). Substitui o cartão de nota do Painel de Gestão, que saiu, e
 * tem duas peças com lugar fixo:
 *
 * - `LotStatus`, no CABEÇALHO, ao lado do total: só o selo da situação, igual
 *   ao de "Meus pagamentos". Não é clique.
 * - `LotActions`, no PÉ do cartão, centralizado: a ação do momento — "Anexar
 *   NF" (ou "Reenviar NF") enquanto a nota não foi aceita, e "Ver pagamento"
 *   depois, que abre o recibo ali mesmo (`MyPaymentModal`, o de "Meus
 *   pagamentos"), sem sair do Início.
 *
 * Sem lote ainda (`undefined`), nada — a competência não fechou para envio.
 */

/** O selo da situação do lote, no cabeçalho do cartão. */
export function LotStatus({ payment: p }: { payment: MyPayment | undefined }) {
  if (p === undefined) return null;
  return <PaymentStatusBadge status={p.status} />;
}

/**
 * A ação do lote, no pé do cartão.
 *
 * - **Nota a enviar** (`aguardando_nf`, ou `pendente` quando o Financeiro pediu
 *   correção): "Anexar NF"/"Reenviar NF", que abre o formulário de sempre
 *   (`InvoiceForm`). Com o envio fechado, desabilitado, e a frase do servidor
 *   (`sendRefusal`) escrita embaixo — quem decide se pode é a API, nunca a tela.
 * - **Depois**: "Ver pagamento". O lote sem nota (consultor interno,
 *   `invoiceRequired` falso) cai direto aqui: a premiação dele nasce sozinha no
 *   Financeiro.
 */
export function LotActions({
  payment: p,
  month,
  canSend,
}: {
  payment: MyPayment | undefined;
  month: string;
  canSend: boolean;
}) {
  const queryClient = useQueryClient();
  const [sending, setSending] = useState(false);
  const [viewing, setViewing] = useState(false);

  if (p === undefined) return null;

  const awaiting =
    p.invoiceRequired && (p.status === 'aguardando_nf' || p.status === 'pendente');

  if (awaiting) {
    const refusal = p.sendRefusal;
    return (
      <>
        <div className="flex flex-col items-center gap-1">
          <Button
            size="sm"
            variant="contorno"
            disabled={refusal !== null}
            onClick={() => setSending(true)}
            className="w-3/4"
          >
            <Icon nome="clipe" size={16} />
            {p.status === 'pendente' ? 'Reenviar NF' : 'Anexar NF'}
          </Button>
          {refusal !== null && (
            <span className="text-center text-xs text-texto-suave">{refusal}</span>
          )}
        </div>

        <Modal
          open={sending}
          onOpenChange={setSending}
          title={`Recorrência ${p.category}`}
        >
          {p.status === 'pendente' && p.correctionReason && (
            <Alert tone="alerta" title="O Financeiro pediu correção">
              {p.correctionReason}
            </Alert>
          )}
          <InvoiceAmount payment={p} />
          <InvoiceForm
            variant="lote"
            payment={p}
            onSent={(sent) => {
              queryClient.setQueryData<MyPayments>(
                ['management', 'payments', 'mine', month],
                (before) =>
                  before?.estado === 'consultor'
                    ? {
                        ...before,
                        items: before.items.map((i) => (i.id === sent.id ? sent : i)),
                        pending: before.pending.map((i) => (i.id === sent.id ? sent : i)),
                      }
                    : before,
              );
              setSending(false);
            }}
          />
        </Modal>
      </>
    );
  }

  return (
    <>
      <div className="flex justify-center">
        <Button
          size="sm"
          variant="contorno"
          onClick={() => setViewing(true)}
          className="w-3/4"
        >
          <Icon nome="documento" size={16} />
          Ver pagamento
        </Button>
      </div>
      {viewing && (
        <MyPaymentModal
          id={p.id}
          canSend={canSend}
          onClose={() => setViewing(false)}
          onChanged={() =>
            void queryClient.invalidateQueries({
              queryKey: ['management', 'payments', 'mine'],
            })
          }
        />
      )}
    </>
  );
}

/**
 * O valor da nota, em destaque, e de onde ele vem — a referência do dono
 * (28/10/2026): a faixa verde com o valor a emitir, e embaixo o acumulado do
 * lote e o que se recebe. A taxa de tecnologia aparece entre os dois quando
 * desconta, senão a diferença ficaria sem explicação.
 */
function InvoiceAmount({ payment: p }: { payment: MyPayment }) {
  const net = fromColumn(p.amount)!;
  const fee =
    p.technologyFee.state === 'aplicada' && p.technologyFee.amount !== null
      ? fromColumn(p.technologyFee.amount)!
      : null;
  const gross = fee === null ? net : sum(net, fee);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col items-center gap-1 rounded-cartao border border-sucesso/30 bg-sucesso-suave px-4 py-5 text-center">
        <span className="rotulo text-sucesso">Gere a nota fiscal no valor de</span>
        <span className="text-xl font-extrabold tabular-nums text-texto">
          {format(net)}
        </span>
      </div>
      <dl className="flex flex-col divide-y divide-borda-suave rounded-cartao border border-borda text-sm">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <dt className="rotulo flex items-center gap-2 text-texto-suave">
            <Icon nome="grafico" size={16} />
            Total acumulado
          </dt>
          <dd className="font-bold tabular-nums">{format(gross)}</dd>
        </div>
        {fee !== null && fee !== 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <dt className="rotulo text-texto-suave">Taxa de tecnologia</dt>
            <dd className="font-bold tabular-nums text-erro">− {format(fee)}</dd>
          </div>
        )}
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <dt className="rotulo flex items-center gap-2 text-texto-suave">
            <Icon nome="dinheiro" size={16} className="text-sucesso" />
            Total a receber
          </dt>
          <dd className="font-bold tabular-nums text-sucesso">{format(net)}</dd>
        </div>
      </dl>
    </div>
  );
}
